import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import config from '../../config';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import Cookies from 'js-cookie';
import {
    Page,
    NamedRedirect,
    LayoutSingleColumn,
    LayoutWrapperTopbar,
    LayoutWrapperMain,
    LayoutWrapperFooter,
    Footer,
    ModalPortal,
    TermsOfService,
    AuthForms,
    ConfirmAuthBlock,
    Alert,
} from '../../components';
import { TopbarContainer } from '../../containers';
import {
    login,
    authenticationInProgress,
    signup,
    signupWithIdp,
    signupWithNativeIdp,
} from '../../ducks/Auth.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { allocateCreditsOnReferralSignUp, fetchListingById } from './AuthenticationPage.duck';
import css from './AuthenticationPage.css';
import EmailVerification from './EmailVerification';
import { LISTING_PAGE_PARAM_TYPE_DRAFT } from '../../components/EditListingWizard/config';
import { createSlug, parse } from '../../util/urlHelpers';
import {
    getEntryDataToRedirectAfterAuth,
    removeEntryDataToRedirectAfterAuth,
    getTempListingLink,
    getUTMSessionStorageData,
    removeTempListingLink,
    setUTMDataToSessionStorage,
    TEMP_LISTING_SLUG_DATA_KEY,
} from '../../util/localStorage';
import { ReviewsWidget } from './ReviewsWidget';
import { withOneSignal } from '../../util/contextHelpers';
import routeConfiguration from '../../routeConfiguration';
import { matchPathname } from '../../util/routes';

const decorateWithExtraParams = func => (...args) => extraParams => {
    const [firstArg, ...restArgs] = args;
    const isValidParams = typeof firstArg === 'object';
    const isValidExtraParams = typeof extraParams === 'object';

    if (isValidParams && isValidExtraParams) {
        const paramsAltered = [{ ...firstArg, ...extraParams }, ...restArgs];
        return func.call(null, ...paramsAltered);
    }
    return func.call(null, ...args);
};

export class AuthenticationPageComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            tosModalOpen: false,
            authError: Cookies.get('st-autherror')
                ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
                : null,
            authInfo: Cookies.get('st-authinfo')
                ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
                : null,
        };
        this.recaptchaRef = React.createRef();
    }
    componentDidMount() {
        // Remove the autherror cookie once the content is saved to state
        // because we don't want to show the error message e.g. after page refresh
        Cookies.remove('st-autherror');
        /** handle cases when login\signup is carried out
         * inside the mobile app not in the browser
         * document.addEventListener - is for android WebView
         * window.addEventListener - is for iOS WebView
         */
        document.addEventListener('message', this.handleNativeMessage);
        window.addEventListener('message', this.handleNativeMessage);

        const listingId = new URL(window.location.href).searchParams.get('listingId');

        if (listingId) {
            this.props.fetchListing(listingId);
        }
        /** for case whean a user lands on a page directly */
        setUTMDataToSessionStorage();

        const utmDataIncluded = getUTMSessionStorageData();

        if (utmDataIncluded) {
            this.setState({
                utmDataIncluded,
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('message', this.handleNativeMessage);
        window.removeEventListener('message', this.handleNativeMessage);
    }

    // check for relevant message event and trigger login
    handleNativeMessage = e => {
        try {
            const socialResponse = typeof e.data === 'string' ? JSON.parse(e.data) : {};

            if (
                socialResponse.idpId === 'facebook' ||
                socialResponse.idpId === 'google' ||
                socialResponse.idpId === 'apple'
            ) {
                const {
                    submitSingupWithNativeIdp: submitSingupWithNativeIdpProp,
                    location,
                } = this.props;
                const { referral } = parse(location.search);
                const { utmDataIncluded } = this.state;
                // recieve response to complete login/registration on sharetribe
                const submitSingupWithNativeIdp = (...args) =>
                    decorateWithExtraParams(submitSingupWithNativeIdpProp)(...args)(
                        utmDataIncluded ? { privateData: { ...utmDataIncluded } } : null
                    );

                submitSingupWithNativeIdp(socialResponse, referral).then(userData => {
                    if (!referral || !userData || !userData.id) return;
                    allocateCreditsOnReferralSignUp(referral, userData.id.uuid);
                });
            }
        } catch (error) {
            console.warn('Unable to parse window message event data', error);
        }
    };

    render() {
        const {
            authInProgress,
            currentUser,
            intl,
            isAuthenticated,
            location,
            loginError,
            scrollingDisabled,
            signupError,
            submitLogin,
            confirmError,
            tab = 'signup',
            sendVerificationEmailInProgress,
            sendVerificationEmailError,
            onResendVerificationEmail,
            listingData,
            listingDataFetchingInProgress,
            tempDataListingRequestSuccess,
            tmpDLPreflightRequestInProgress,
            submitSignup: submitSignupProp,
            submitSingupWithIdp: submitSingupWithIdpProp,
        } = this.props;

        const isConfirm = tab === 'confirm';
        const isLogin = tab === 'login';
        const isEmailVerification = tab === 'email-verification';
        const isSignup = tab === 'signup';
        const isSignupRider = tab === 'signup-rider';
        const isSignupHorseowner = tab === 'signup-horse-owner';

        const currentPageData = matchPathname(location.pathname, routeConfiguration());

        const locationFrom = location.state && location.state.from ? location.state.from : null;
        const authinfoFrom =
            this.state.authInfo && this.state.authInfo.from ? this.state.authInfo.from : null;
        const from = locationFrom ? locationFrom : authinfoFrom ? authinfoFrom : null;

        const searchParsed = parse(location.search);
        const { tempListingInfoSaved, referral } = searchParsed;

        const { utmDataIncluded } = this.state;

        const entryToRedirectAfterAuth = getEntryDataToRedirectAfterAuth();

        const submitSignup = (...args) =>
            decorateWithExtraParams(submitSignupProp)(...args)(
                utmDataIncluded ? { privateData: { ...utmDataIncluded } } : null
            );

        const submitSingupWithIdp = (...args) =>
            decorateWithExtraParams(submitSingupWithIdpProp)(...args)(
                utmDataIncluded ? { privateData: { ...utmDataIncluded } } : null
            );

        const user = ensureCurrentUser(currentUser);
        const currentUserLoaded = !!user.id;

        const { [TEMP_LISTING_SLUG_DATA_KEY]: tempListing } = getTempListingLink() || {};

        if (isAuthenticated && tempDataListingRequestSuccess && tempListing) {
            /**
             * user being logged out finished 1st step of listing editing;
             * redirect him after he finishes onboarding flow with user type horseowner
             */
            removeTempListingLink();

            return tempListing.id ? (
                <NamedRedirect
                    name="EditListingPage"
                    params={{
                        id: tempListing.id.uuid,
                        slug: createSlug(tempListing),
                        type: LISTING_PAGE_PARAM_TYPE_DRAFT,
                        tab: 'overview',
                    }}
                />
            ) : (
                <NamedRedirect name="LandingPage" />
            );
        }
        // We only want to show the email verification dialog in the signup
        // tab if the user isn't being redirected somewhere else
        // (i.e. `from` is present). We must also check the `emailVerified`
        // flag only when the current user is fully loaded.
        const showEmailVerification =
            isEmailVerification && currentUserLoaded && !user.attributes.emailVerified;
        // Already authenticated, redirect away from auth page
        const notAuthenticatedForEmailVerification = !isAuthenticated && isEmailVerification;

        const baseCaseRedirect =
            isAuthenticated &&
            currentUserLoaded &&
            !showEmailVerification &&
            !tmpDLPreflightRequestInProgress &&
            (tempListing ? tempDataListingRequestSuccess : true);

        const redirectToListingPageFromSearchParams =
            baseCaseRedirect &&
            entryToRedirectAfterAuth &&
            entryToRedirectAfterAuth?.params &&
            entryToRedirectAfterAuth?.name;

        /**
         * tempDataListingRequestSuccess do not redirect user until
         * temp listing is created (if  needed)
         */
        if (
            isAuthenticated &&
            !isEmailVerification &&
            from &&
            (tempListing ? tempDataListingRequestSuccess : true)
        ) {
            // do not redirect a user to the pages which also have
            // redirection cases, e.g. onboarding page

            // WARNING !!!
            // before change the if statement
            // address to the redirectFromCollectingInfo variable at Routes.js file
            return <Redirect to={from} />;
        } else if (redirectToListingPageFromSearchParams) {
            // redirect either to ListingPage or ProfilePage;
            // the listing data is set in case there is listingId search param;
            // see. useSetListingDataToStorage.js;
            removeEntryDataToRedirectAfterAuth();

            return <NamedRedirect {...entryToRedirectAfterAuth} />;
        } else if (baseCaseRedirect) {
            // base case
            // user auth succeeded
            // no extra logic for redirecting to other pages
            return <NamedRedirect name="LandingPage" />;
        } else if (notAuthenticatedForEmailVerification) {
            return <NamedRedirect name="SignupPage" params={{ tab: 'signup' }} />;
        }

        const loginErrorMessage = (
            <div className={css.error}>
                <span>{intl.formatMessage({ id: 'AuthenticationPage.loginFailed' })} </span>
            </div>
        );

        const signupErrorMessage = (
            <div className={css.error}>
                <span>
                    {intl.formatMessage({ id: 'AuthenticationPage.signupFailedEmailAlreadyTaken' })}{' '}
                </span>
            </div>
        );

        // eslint-disable-next-line no-confusing-arrow
        const errorMessage = (error, message) => (error ? message : null);
        const loginOrSignupError = isLogin
            ? errorMessage(loginError, loginErrorMessage)
            : errorMessage(signupError, signupErrorMessage);

        const idp = this.state.authInfo
            ? this.state.authInfo.idpId.replace(/^./, str => str.toUpperCase())
            : null;

        const formContent = isConfirm ? (
            <ConfirmAuthBlock
                idp={idp}
                authInfo={this.state.authInfo}
                authInProgress={authInProgress}
                submitSingupWithIdp={submitSingupWithIdp}
                confirmError={confirmError}
                currentUser={currentUser}
            />
        ) : (
            <AuthForms
                tab={tab}
                error={loginOrSignupError}
                submitLogin={submitLogin}
                submitSignup={submitSignup}
                authInProgress={authInProgress}
                from={from}
                onOpenTermsOfService={() => this.setState({ tosModalOpen: true })}
                listingData={listingData}
                listingDataFetchingInProgress={listingDataFetchingInProgress}
            />
        );

        const siteTitle = config.siteTitle;
        const schemaTitle = isLogin
            ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { siteTitle })
            : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { siteTitle });

        const topbarClasses = classNames({
            [css.hideOnMobile]: showEmailVerification,
        });

        const hideBlobBackground = isSignupRider || isSignupHorseowner;
        const ogProps = referral
            ? {
                  ogDescription: `Nutze meinen Link, um kostenlos Mitglied zu werden und 100 Credits zu erhalten.`,
                  ogTitle: 'Werde Mitglied von HorseDeal 🐴💜',
              }
            : {};

        return (
            <Page
                title={schemaTitle}
                scrollingDisabled={scrollingDisabled}
                schema={{
                    '@context': 'http://schema.org',
                    '@type': 'WebPage',
                    name: schemaTitle,
                }}
                {...ogProps}
            >
                <LayoutSingleColumn>
                    <LayoutWrapperTopbar>
                        <TopbarContainer
                            className={topbarClasses}
                            currentPage={
                                currentPageData &&
                                currentPageData[0] &&
                                currentPageData[0]?.route?.name
                            }
                        />
                    </LayoutWrapperTopbar>
                    <LayoutWrapperMain
                        className={classNames(css.layoutWrapperMain, {
                            [css.hideBlobBackground]: hideBlobBackground,
                        })}
                        blobBackground
                    >
                        {tempListingInfoSaved && (
                            <Alert
                                header="AuthenticationPage.tempListingInfoSavedHeading"
                                description="AuthenticationPage.tempListingInfoSavedDesc"
                                rootClassName={css.tempListingInfoSaved}
                                closeClassName={css.tempListingInfoSavedCloseBtn}
                                type="success"
                            />
                        )}
                        <div className={css.root}>
                            {showEmailVerification ? (
                                <EmailVerification
                                    user={user}
                                    sendVerificationEmailError={sendVerificationEmailError}
                                    sendVerificationEmailInProgress={
                                        sendVerificationEmailInProgress
                                    }
                                    onResendVerificationEmail={onResendVerificationEmail}
                                />
                            ) : (
                                formContent
                            )}
                        </div>
                        {isSignup && <ReviewsWidget />}
                        <ModalPortal
                            id="AuthenticationPage.tos"
                            isOpen={this.state.tosModalOpen}
                            onClose={() => this.setState({ tosModalOpen: false })}
                        >
                            <div className={css.termsWrapper}>
                                <h2 className={css.termsHeading}>
                                    <FormattedMessage id="AuthenticationPage.termsHeading" />
                                </h2>
                                <TermsOfService />
                            </div>
                        </ModalPortal>
                    </LayoutWrapperMain>
                    <LayoutWrapperFooter>
                        <Footer />
                    </LayoutWrapperFooter>
                </LayoutSingleColumn>
            </Page>
        );
    }
}

const { bool, func, object, oneOf, shape } = PropTypes;

AuthenticationPageComponent.propTypes = {
    authInProgress: bool.isRequired,
    currentUser: propTypes.currentUser,
    isAuthenticated: bool.isRequired,
    loginError: propTypes.error,
    scrollingDisabled: bool.isRequired,
    signupError: propTypes.error,
    submitLogin: func.isRequired,
    submitSignup: func.isRequired,
    tab: oneOf([
        'login',
        'signup',
        'email-verification',
        'confirm',
        'signup-rider',
        'signup-horse-owner',
    ]),

    sendVerificationEmailInProgress: bool.isRequired,
    sendVerificationEmailError: propTypes.error,
    onResendVerificationEmail: func.isRequired,

    // from withRouter
    location: shape({ state: object }).isRequired,

    // from injectIntl
    intl: intlShape.isRequired,
};

const mapStateToProps = state => {
    const {
        isAuthenticated,
        loginError,
        signupError,
        confirmError,
        tempDataListingRequestSuccess,
        tmpDLPreflightRequestInProgress,
    } = state.Auth;
    const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
    const { listingData, listingDataFetchingInProgress } = state.AuthenticationPage;

    return {
        authInProgress: authenticationInProgress(state),
        currentUser,
        isAuthenticated,
        loginError,
        scrollingDisabled: isScrollingDisabled(state),
        confirmError,
        signupError,
        confirmError,
        sendVerificationEmailInProgress,
        sendVerificationEmailError,
        listingData,
        listingDataFetchingInProgress,
        tempDataListingRequestSuccess,
        tmpDLPreflightRequestInProgress,
    };
};

const mapDispatchToProps = dispatch => ({
    fetchListing: listingId => dispatch(fetchListingById(listingId)),
    submitLogin: ({ email, password, callback }) => dispatch(login(email, password, callback)),
    onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
    submitSignup: (params, paramsToRedirect, referral) =>
        dispatch(signup(params, paramsToRedirect, referral)),
    submitSingupWithIdp: (params, referral) => dispatch(signupWithIdp(params, referral)),
    submitSingupWithNativeIdp: (params, referral) =>
        dispatch(signupWithNativeIdp(params, referral)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
    withRouter,
    withOneSignal,
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
)(AuthenticationPageComponent);

export default AuthenticationPage;
